import React from 'react';
import {Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import TakeActionFlow from '../../DQPro/PerformanceSummary/TakeActionFlow.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, cellText} from '../schoolData';
import {NAVY, TEAL, PageHeader, headCell, bodyCell} from '../components/widgets.comp';
import {EvidenceCell, RecommendationCell} from '../../DashboardCommon/RecommendationCells.comp';
import {isScopeSuppressed} from '../../DashboardCommon/dashboardCards';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Rows for groups under the n floor are not built by the datahouse (D-32): a school with fewer than 10 assessed
   students has none, and the empty state says why; Take Action needs a row to act on. */
const SUPPRESSED_EMPTY = 'Priority actions are not shown for groups of fewer than 10 students.';
const NO_MATCH = 'No row matches the filters.';

/* Slide 34 — Recommended Learning Programs. The Target Group rows (Whole
   School, Grade 5 x5, Class A) and blank Baseline Evidence / Recommended
   Learning columns are copied verbatim from the deck's own table — there is
   no recommendation engine behind this yet, so real values are left as em
   dashes rather than invented (same convention as
   src/pages/DQPro/PerformanceSummary/PerformanceSummary.page.jsx's identical
   recommendation table). "Take Action" is the only real button on this
   slide (shape fill 188038) — "Full list will be shown." and "Evidence &
   Recommendation logic is stored in a Google Doc." are yellow annotation
   boxes (instructions for the build), not literal on-page copy, so neither
   is rendered. The linked Google Doc
   (docs.google.com/document/d/19S3sxqK9ns...) returns 401 Unauthorized
   regardless. Table styling matches this dashboard's own headCell/bodyCell
   convention (used by every other table here), not the mockup's own colors. */
/* Live rows: SD-34-01 → SD.S_20.C_2 (PriorityTableCard). The card's
   Target Group, Baseline Evidence and Recommended Learning columns fill this table as
   laid out on the slide; the card's extra Priority column is not on the slide.
   Every column sorts from its header; the Target Group filters once there are
   enough rows (QA 9 Oct 2026). */
const COLUMNS = [
    {key: 'targetGroup', label: 'Target Group', type: 'text'},
    // Slide 34 renames the card's Baseline Evidence column (Ian, 1 Oct). Both list cells sort by their text; no checklist.
    {key: 'evidence', label: 'Areas of Weakness', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.evidence)},
    {key: 'recommendation', label: 'Recommended Learning', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.recommendation)},
];

const RecommendationTable = ({rows, emptyText = 'No learning program recommendations yet.'}) => {
    const t = useTableControls(rows, COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: 'left', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.targetGroup}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.targetGroup}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><EvidenceCell value={row._raw?.evidence} fallback={row.evidence}/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><RecommendationCell row={row}/></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PRIORITY_ACTIONS_LP_SECTION_CODE = 'S_20';
const CARD_SECTIONS = [PRIORITY_ACTIONS_LP_SECTION_CODE];

const PriorityActionsLearningProgramPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = findCard(dashboardData, PRIORITY_ACTIONS_LP_SECTION_CODE, 'C_2');
    const rows = rowsFrom(card, {targetGroup: 'Target Group', evidence: 'Baseline Evidence', recommendation: 'Recommended Learning'});

    return (
    <SchoolDashboardShell>
        <PageHeader
            title="Recommended Learning Programs"
            subtitle="The crosswalk converts priority COS areas into standard action families, concrete interventions, and deployment status."
        />

        {card?.take_action !== false && rows.length > 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2.5}}>
            {/* Opens the SW/SNW-P1A or -P1B Performance Summary Take Action flow (see TakeActionFlow). */}
            <TakeActionFlow>
                {(openTakeAction) => (
                    <Button onClick={openTakeAction} sx={{textTransform: 'none', fontWeight: 700, backgroundColor: TEAL, color: '#fff', '&:hover': {backgroundColor: TEAL}}}>
                        Take Action
                    </Button>
                )}
            </TakeActionFlow>
        </Box>
        )}

        <RecommendationTable rows={rows} emptyText={rows.length ? NO_MATCH : (isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : undefined)}/>
    </SchoolDashboardShell>
    );
};

export default PriorityActionsLearningProgramPage;
